import {createSelector, createSlice, PayloadAction} from 'lowkey-component';

import {
  MediaQueryScreenMode,
  OneViewStatus,
  Orientation,
  PageIndicatorStatus,
  SiteIndicatorStatus,
} from '@services/types';
import {RootState} from '@store/rootReducer';

export interface AppSlice {
  isOpenGlobalNav: boolean;
  mediaQueryScreenMode: MediaQueryScreenMode | undefined;
  orientation: Orientation | undefined;
  pageIndicatorStatus: PageIndicatorStatus;
  siteIndicatorStatus: SiteIndicatorStatus;
  siteIndicatorPercent: number;
  oneViewStatus: OneViewStatus;
  isScrollStop: boolean;
  videoIsFullscreen: boolean;
}

const initialValues: AppSlice = {
  isOpenGlobalNav: false,
  mediaQueryScreenMode: undefined,
  orientation: undefined,
  pageIndicatorStatus: 'hide',
  siteIndicatorStatus: 'show',
  siteIndicatorPercent: 0,
  oneViewStatus: 'up',
  isScrollStop: false,
  videoIsFullscreen: false,
};

export const selectAppState = (state: RootState) => {
  return state.app;
};

const selectPageIndicatorStatus = createSelector(
  selectAppState,
  ({pageIndicatorStatus}) => pageIndicatorStatus,
);

const selectSiteIndicatorStatus = createSelector(
  selectAppState,
  ({siteIndicatorStatus}) => siteIndicatorStatus,
);

const selectSiteIndicatorPercent = createSelector(
  selectAppState,
  ({siteIndicatorPercent}) => siteIndicatorPercent,
);

const selectMediaQueryScreenMode = createSelector(
  selectAppState,
  ({mediaQueryScreenMode}) => mediaQueryScreenMode,
);

const selectOrientation = createSelector(
  selectAppState,
  ({orientation}) => orientation,
);

const selectIsOpenGlobalNav = createSelector(
  selectAppState,
  ({isOpenGlobalNav}) => isOpenGlobalNav,
);

const selectIsScrollStop = createSelector(
  selectAppState,
  ({isScrollStop}) => isScrollStop,
);

const selectOneViewStatus = createSelector(
  selectAppState,
  ({oneViewStatus}) => oneViewStatus,
);

const selectVideoIsFullscreen = createSelector(
  selectAppState,
  ({videoIsFullscreen}) => videoIsFullscreen,
);

const appSlice = createSlice({
  initialValues,
  reducers: {
    isOpenGlobalNavSaved: (state, action: PayloadAction<boolean>) => {
      state.isOpenGlobalNav = action.payload;
    },
    mediaQueryScreenModeSaved: (
      state,
      action: PayloadAction<MediaQueryScreenMode>,
    ) => {
      state.mediaQueryScreenMode = action.payload;
    },
    orientationSaved: (state, action: PayloadAction<Orientation>) => {
      state.orientation = action.payload;
    },
    pageIndicatorStatusSaved: (
      state,
      action: PayloadAction<PageIndicatorStatus>,
    ) => {
      state.pageIndicatorStatus = action.payload;
    },
    siteIndicatorStatusSaved: (
      state,
      action: PayloadAction<SiteIndicatorStatus>,
    ) => {
      state.siteIndicatorStatus = action.payload;
    },
    siteIndicatorPercentSaved: (state, action: PayloadAction<number>) => {
      state.siteIndicatorPercent = action.payload;
    },
    oneViewStatusSaved: (state, action: PayloadAction<OneViewStatus>) => {
      state.oneViewStatus = action.payload;
    },
    isScrollStopSaved: (state, action: PayloadAction<boolean>) => {
      state.isScrollStop = action.payload;
    },
    videoIsFullscreenSaved: (state, action: PayloadAction<boolean>) => {
      state.videoIsFullscreen = action.payload;
    },
  },
});

export const appSelectors = {
  selectIsOpenGlobalNav,
  selectMediaQueryScreenMode,
  selectOrientation,
  selectPageIndicatorStatus,
  selectSiteIndicatorStatus,
  selectSiteIndicatorPercent,
  selectOneViewStatus,
  selectIsScrollStop,
  selectVideoIsFullscreen,
};

export const {
  isOpenGlobalNavSaved,
  mediaQueryScreenModeSaved,
  orientationSaved,
  pageIndicatorStatusSaved,
  siteIndicatorStatusSaved,
  siteIndicatorPercentSaved,
  oneViewStatusSaved,
  isScrollStopSaved,
  videoIsFullscreenSaved,
} = appSlice.actions;

export const {getInitialValues} = appSlice;
export default appSlice.reducer;
